import { Component, OnInit, ChangeDetectorRef } from '@angular/core';
import { MatDialogRef } from '@angular/material/dialog';
import {
    FormControl,
    FormGroup,
    Validators,
    AbstractControl,
    FormArray,
} from '@angular/forms';
import { PatientService } from '../../services/patient.service';
import { IStatementParam } from '../../interfaces/patient';
import { LoadService } from '../../services/application/load.service';
import moment from 'moment';
import { AlertService } from 'src/lib/ariadna/alert';
import { Router } from '@angular/router';
import { MAT_DATE_FORMATS, MAT_DATE_LOCALE } from '@angular/material/core';
import { Subscription } from 'rxjs';
import { FormBuilder } from '@angular/forms';

export const RU_DATE_FORMATS = {
    parse: {
        dateInput: 'DD.MM.YYYY',
    },
    display: {
        dateInput: 'DD.MM.YYYY',
        monthYearLabel: 'MMM YYYY',
        dateA11yLabel: 'DD.MM.YYYY',
        monthYearA11yLabel: 'MMMM YYYY',
    },
};

// Максимальное количество пациентов, которое можно добавить в заявление в справку в налоговую
export const MAX_PATIENTS = 5;

@Component({
    selector: 'app-statement',
    templateUrl: './statement.component.html',
    styleUrls: ['./statement.component.scss'],
    providers: [
        { provide: MAT_DATE_FORMATS, useValue: RU_DATE_FORMATS },
        { provide: MAT_DATE_LOCALE, useValue: 'ru' },
    ],
})
export class StatementComponent implements OnInit {
    readonly MAX_PATIENTS = MAX_PATIENTS;
    public frmStatement!: FormGroup;
    public loading = false;
    public loadingFrm = false;
    public btn: number[] = [1, 0, 0, 0];
    public btnYearTxt: string[] = [];
    public selectedYear: number | null = null;
    public selectedIndexYear: number | null = null;
    public maxDate = moment(new Date()).format('YYYY-MM-DD');
    public showFormOk = false;
    public INFO_HEADER_STATEMENT =
        'Информацию о готовности справки вы можете получить по телефону: 8 800 888 88 88';
    public INFO_BODY_STATEMENT = '';
    private _setting: any;
    public params: IStatementParam[] | null = null;
    public patientBd = new Date();
    public unfilledFields: string[] = [];
    public unfilledFieldsDetails: { key: string; label: string }[] = [];
    public showUnfilledFieldsList: boolean = false;
    public isMobile: boolean = false;
    showConfirmation = false;
    private _subscription: Subscription | null = null;

    constructor(
        public dialogRef: MatDialogRef<StatementComponent>,
        private router: Router,
        private patientS: PatientService,
        private load: LoadService,
        private alertService: AlertService,
        private cdr: ChangeDetectorRef,
        private fb: FormBuilder,
    ) {
        this._setting = load.configData.pages.personal;
        this.INFO_HEADER_STATEMENT =
            this._setting.WEB_LK_INFO_HEADER_STATEMENT ||
            this.INFO_HEADER_STATEMENT;
        this.INFO_BODY_STATEMENT =
            this._setting.WEB_LK_INFO_BODY_STATEMENT ||
            this.INFO_BODY_STATEMENT;

        this.dialogRef.disableClose = true;

        this.dialogRef.backdropClick().subscribe((event) => {
            event?.stopImmediatePropagation?.();
            this.attemptClose();
        });

        this.dialogRef.keydownEvents().subscribe((event) => {
            if (event.key === 'Escape') {
                event.preventDefault();
                event.stopPropagation();
                this.attemptClose();
            }
        });
    }

    get patientsFormArray(): FormArray {
        return this.frmStatement.get('patients') as FormArray;
    }

    ngOnInit() {
        this.initialize();
        this.isMobile = window.innerWidth <= 767;
        window.addEventListener('resize', this.resizeHandler);
    }

    ngOnDestroy() {
        window.removeEventListener('resize', this.resizeHandler);
        if (this._subscription) {
            this._subscription.unsubscribe();
        }
    }

    private resizeHandler = () => {
        this.isMobile = window.innerWidth <= 767;
        this.cdr.detectChanges();
    };

    private initialize(): void {
        this.btnYearTxt = [
            moment(new Date()).format('YYYY'),
            moment(new Date()).subtract(1, 'years').format('YYYY'),
            moment(new Date()).subtract(2, 'years').format('YYYY'),
            moment(new Date()).subtract(3, 'years').format('YYYY'),
        ];
        this.initializeFrm();
    }

    private initializeFrm(): void {
        this.frmStatement = this.fb.group({
            nalog_fio: ['', [Validators.minLength(3), this.validateFIO]],
            nalog_inn: ['', [Validators.minLength(12)]],
            nalog_passport_series: [
                '',
                [Validators.required, Validators.minLength(4)],
            ],
            nalog_passport_number: [
                '',
                [Validators.required, Validators.minLength(6)],
            ],
            nalog_passport_date: [
                '',
                [Validators.required, Validators.minLength(5)],
            ],
            patients: this.fb.array([this.createPatientFormGroup(1)]),

            selectedYear: [null, Validators.required],
            is_recovery: [false],
            dov_fio: [
                '',
                [
                    Validators.minLength(3),
                    this.validateFIO,
                    Validators.required,
                ],
            ],
            dov_phone: [
                '',
                [
                    Validators.pattern(/^\(\d{3}\)\s\d{3}-\d{4}$/),
                    Validators.required,
                ],
            ],
            email: ['', [Validators.required, Validators.email]],
            is_taxpayer_patient: [false],
        });

        this.loadingFrm = true;

        this.frmStatement.valueChanges.subscribe(() => {
            if (this.frmStatement.pristine) {
                this.frmStatement.markAsDirty();
            }
            this.cdr.detectChanges();
        });
    }

    private createPatientFormGroup(id: number) {
        return this.fb.group({
            id: [id],
            fio: [
                '',
                [
                    Validators.required,
                    Validators.minLength(3),
                    this.validateFIO,
                ],
            ],
            dr: ['', [Validators.required, Validators.minLength(5)]],
            passportSeries: [
                '',
                [Validators.required, Validators.minLength(4)],
            ],
            passportNumber: [
                '',
                [Validators.required, Validators.minLength(6)],
            ],
            passportDate: ['', [Validators.required, Validators.minLength(5)]],
        });
    }

    public addPatient() {
        if (this.patientsFormArray.length < MAX_PATIENTS) {
            const nextId = this.patientsFormArray.length + 1;
            this.patientsFormArray.push(this.createPatientFormGroup(nextId));
        }
    }

    public disableAddPatient(): boolean {
        if (!this.loadingFrm || this.patientsFormArray.length >= MAX_PATIENTS) {
            return true;
        }

        const lastPatientIndex = this.patientsFormArray.length - 1;
        const lastPatient = this.patientsFormArray.at(
            lastPatientIndex,
        ) as FormGroup;

        const requiredFields = [
            'fio',
            'dr',
            'passportSeries',
            'passportNumber',
            'passportDate',
        ];
        const isLastPatientValid = requiredFields.every((field) => {
            const control = lastPatient.get(field);
            return control?.valid && control?.value?.toString().trim() !== '';
        });

        return !isLastPatientValid;
    }

    public delPatient(index: number) {
        if (index > 0) {
            this.patientsFormArray.removeAt(index);
        }
    }

    public canAddPatient(): boolean {
        return this.patientsFormArray.length < MAX_PATIENTS;
    }

    public trackByIndex(index: number): number {
        return index;
    }

    private isFormModified(): boolean {
        return this.frmStatement.dirty;
    }

    attemptClose(): void {
        if (this.showFormOk) {
            this.dialogRef.close();
            return;
        }
        if (this.isFormModified()) {
            this.showConfirmation = true;
            setTimeout(() => this.cdr.detectChanges(), 0);
        } else {
            this.dialogRef.close();
        }
    }

    cancelClose(): void {
        this.showConfirmation = false;
        this.cdr.detectChanges();
    }

    confirmClose(): void {
        this.dialogRef.close();
    }

    private validateFIO(
        control: AbstractControl,
    ): { [key: string]: any } | null {
        const fio = control.value;
        if (!fio) return null;
        const firstCharIsValid = /^[a-zA-Zа-яА-Я']/.test(fio);
        const validChars = /^[a-zA-Zа-яА-Я'\-\s]+$/.test(fio);
        return !firstCharIsValid || !validChars ? { invalidFIO: true } : null;
    }

    public clicBtn(index: number): void {
        this.selectedIndexYear = index;
        this.btn = this.btn.map(() => 0);
        this.btn[index] = 1;
        this.selectedYear = +this.btnYearTxt[index];
        this.frmStatement.get('selectedYear')?.setValue(this.selectedYear);
    }

    public isFormValid(): boolean {
        return this.frmStatement.valid;
    }

    public save(): void {
        this.frmStatement.markAllAsTouched();
        if (!this.isFormValid()) {
            this.alertService.error(
                'Пожалуйста, заполните все обязательные поля',
                5000,
            );
            const unfilledFieldsList = this.getUnfilledFields().join(', ');
            if (unfilledFieldsList) {
                this.alertService.error(
                    `Заполните следующие поля: ${unfilledFieldsList}`,
                    5000,
                );
            }
            return;
        }

        this.loading = true;
        this.patientS.getStatementSave$(1, this.generXml()).subscribe(
            (info) => {
                this.showFormOk = true;
                this.loading = false;
            },
            (err) => {
                this.loading = false;
                this.alertService.error(
                    'Что-то пошло не так, попробуйте позже.',
                    5000,
                );
            },
        );
    }

    private generXml(): string {
        let xml = '<root>';

        const mainFields = [
            'nalog_fio',
            'nalog_inn',
            'nalog_passport_series',
            'nalog_passport_number',
            'nalog_passport_date',
            'selectedYear',
            'is_recovery',
            'dov_fio',
            'dov_phone',
            'email',
            'is_taxpayer_patient',
        ];

        mainFields.forEach((key) => {
            let value = this.frmStatement.get(key)?.value;
            if (key === 'is_taxpayer_patient' || key === 'is_recovery') {
                value = value ? 'Да' : 'Нет';
            }
            xml += `<item><name>${key.toUpperCase()}</name><value>${value}</value></item>`;
        });

        const patients = this.patientsFormArray.value;
        patients.forEach((p: any, idx: number) => {
            const i = idx + 1;
            xml += `<item><name>PATIENT_FIO${i}</name><value>${p.fio}</value></item>`;
            xml += `<item><name>PATIENT_DR${i}</name><value>${p.dr}</value></item>`;
            xml += `<item><name>PATIENT_PASSPORT_SERIES${i}</name><value>${p.passportSeries}</value></item>`;
            xml += `<item><name>PATIENT_PASSPORT_NUMBER${i}</name><value>${p.passportNumber}</value></item>`;
            xml += `<item><name>PATIENT_PASSPORT_DATE${i}</name><value>${p.passportDate}</value></item>`;
        });

        const yearStr = this.btn
            .map((item, i) => (item ? this.btnYearTxt[i] : ''))
            .filter(Boolean)
            .join(';');
        if (yearStr) {
            xml += `<item><name>YEARE</name><value>${yearStr}</value></item>`;
        }

        xml += '</root>';
        return xml;
    }

    public getUnfilledFields(): string[] {
        const fields: { key: string; label: string }[] = [
            { key: 'nalog_fio', label: 'ФИО налогоплательщика' },
            { key: 'nalog_inn', label: 'ИНН' },
            {
                key: 'nalog_passport_series',
                label: 'Серия паспорта налогоплательщика',
            },
            {
                key: 'nalog_passport_number',
                label: 'Номер паспорта налогоплательщика',
            },
            {
                key: 'nalog_passport_date',
                label: 'Дата выдачи паспорта налогоплательщика',
            },
            { key: 'dov_fio', label: 'ФИО доверенного лица' },
            { key: 'dov_phone', label: 'Телефон доверенного лица' },
            { key: 'email', label: 'Email' },
        ];

        this.patientsFormArray.controls.forEach((_, idx) => {
            const i = idx + 1;
            fields.push(
                { key: `patients.${idx}.fio`, label: `ФИО пациента ${i}` },
                {
                    key: `patients.${idx}.dr`,
                    label: `Дата рождения пациента ${i}`,
                },
                {
                    key: `patients.${idx}.passportSeries`,
                    label: `Серия паспорта пациента ${i}`,
                },
                {
                    key: `patients.${idx}.passportNumber`,
                    label: `Номер паспорта пациента ${i}`,
                },
                {
                    key: `patients.${idx}.passportDate`,
                    label: `Дата выдачи паспорта пациента ${i}`,
                },
            );
        });

        this.unfilledFieldsDetails = fields.filter(({ key, label }) => {
            const parts = key.split('.');
            let control: AbstractControl | null = this.frmStatement;
            for (const part of parts) {
                control = control.get(part);
                if (!control) return false;
            }
            return control.invalid || !control.value;
        });

        this.unfilledFields = this.unfilledFieldsDetails.map((f) => f.label);
        this.showUnfilledFieldsList = this.unfilledFields.length > 0;
        return this.unfilledFields;
    }

    public navigateToHome(): void {
        this.router.navigate(['/']);
        this.dialogRef.close();
    }

    public numberOnly(event: any): boolean {
        const charCode = event.which ? event.which : event.keyCode;
        if (charCode === 48 && event.srcElement.value.length === 0) {
            return false;
        }
        return !(charCode > 31 && (charCode < 48 || charCode > 57));
    }

    public validateDate(event: any): void {
        const inputDate = event.target.value;
        const selectedDate = new Date(inputDate);
        const currentDate = new Date();

        const formControlName = event.target.getAttribute('formcontrolname');
        if (!formControlName) return;

        let control: AbstractControl | null = null;
        if (formControlName.includes('patients')) {
        } else {
            control = this.frmStatement.get(formControlName);
        }

        if (control) {
            if (selectedDate > currentDate) {
                control.setErrors({ invalidDate: true });
            } else {
                control.setErrors(null);
            }
        }
    }

    public onInput(event: Event, type: string): void {
        const input = event.target as HTMLInputElement;
        let maxLength: number;
        switch (type) {
            case 'pasSeries':
                maxLength = 4;
                break;
            case 'pasNumber':
                maxLength = 6;
                break;
            case 'inn':
                maxLength = 12;
                break;
            case 'phoneNumber':
                maxLength = 10;
                break;
            default:
                maxLength = 14;
        }
        let value = input.value.replace(/\D/g, '');
        value = value.slice(0, maxLength);
        input.value = value;
        const controlName = input.getAttribute('formControlName') || '';
        const control = this.frmStatement.get(controlName);
        if (control) {
            control.setValue(value, { emitEvent: true });
            control.markAsTouched();
        }
    }

    private taxpayerPatientSubscription: Subscription | null = null;

    public onTaxpayerPatientChange(event: Event): void {
        const isChecked = (event.target as HTMLInputElement).checked;
        const isTaxpayerCtrl = this.frmStatement.get('is_taxpayer_patient');
        if (!isTaxpayerCtrl) return;

        isTaxpayerCtrl.setValue(isChecked, { emitEvent: false });

        if (this.taxpayerPatientSubscription) {
            this.taxpayerPatientSubscription.unsubscribe();
            this.taxpayerPatientSubscription = null;
        }

        const firstPatient = this.patientsFormArray.at(0) as FormGroup;

        if (isChecked) {
            firstPatient.patchValue(
                {
                    fio: this.frmStatement.get('nalog_fio')?.value || '',
                    passportSeries:
                        this.frmStatement.get('nalog_passport_series')?.value ||
                        '',
                    passportNumber:
                        this.frmStatement.get('nalog_passport_number')?.value ||
                        '',
                    passportDate:
                        this.frmStatement.get('nalog_passport_date')?.value ||
                        '',
                    dr: firstPatient.get('dr')?.value || '',
                },
                { emitEvent: false },
            );

            this.taxpayerPatientSubscription = new Subscription();

            const taxpayerToPatientMap: { [key: string]: string } = {
                nalog_fio: 'fio',
                nalog_passport_series: 'passportSeries',
                nalog_passport_number: 'passportNumber',
                nalog_passport_date: 'passportDate',
            };

            Object.entries(taxpayerToPatientMap).forEach(
                ([taxpayerKey, patientKey]) => {
                    const taxpayerCtrl = this.frmStatement.get(taxpayerKey);
                    const patientCtrl = firstPatient.get(patientKey);
                    if (taxpayerCtrl && patientCtrl) {
                        this.taxpayerPatientSubscription!.add(
                            taxpayerCtrl.valueChanges.subscribe((value) => {
                                patientCtrl.setValue(value, {
                                    emitEvent: false,
                                });
                            }),
                        );
                    }
                },
            );

            const patientToTaxpayerMap: { [key: string]: string } = {
                fio: 'nalog_fio',
                passportSeries: 'nalog_passport_series',
                passportNumber: 'nalog_passport_number',
                passportDate: 'nalog_passport_date',
            };

            Object.entries(patientToTaxpayerMap).forEach(
                ([patientKey, taxpayerKey]) => {
                    const patientCtrl = firstPatient.get(patientKey);
                    const taxpayerCtrl = this.frmStatement.get(taxpayerKey);
                    if (patientCtrl && taxpayerCtrl) {
                        this.taxpayerPatientSubscription!.add(
                            patientCtrl.valueChanges.subscribe((value) => {
                                taxpayerCtrl.setValue(value, {
                                    emitEvent: false,
                                });
                            }),
                        );
                    }
                },
            );
        }

        this.cdr.detectChanges();
    }

    get patientGroups(): FormGroup[] {
        return this.patientsFormArray.controls as FormGroup[];
    }
}
